/**
 * New Conversation Dialog
 * Unified flow: pick a unit, review/adjust its live tenant roster (pre-selected by
 * default — TG-11), optionally connect the conversation to Telegram, and submit.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  createConversation,
  listUnits,
  listTenantsByUnit,
  type UnitWithRelations,
  type TenantWithDetails,
  type CreateConversationInput,
} from '../../lib/api';
import { formatUnitLabel, realUnitNumber } from '@resida/shared';

// Elements a focus trap should cycle through — mirrors Modal.tsx's existing implementation.
const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

interface NewConversationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConversationCreated?: (conversationId: string) => void;
  /** Pre-selects this unit and jumps straight to the tenant/Telegram step (e.g. when
   * opened from Unit Detail Page's "Message this unit's tenants" link). */
  initialUnitId?: string;
}

export function NewConversationDialog({
  isOpen,
  onClose,
  onConversationCreated,
  initialUnitId,
}: NewConversationDialogProps) {
  const [step, setStep] = useState<'unit' | 'tenants'>('unit');
  const [units, setUnits] = useState<UnitWithRelations[]>([]);
  const [tenants, setTenants] = useState<TenantWithDetails[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<string>('');
  const [selectedTenantIds, setSelectedTenantIds] = useState<Set<string>>(new Set());
  const [connectTelegram, setConnectTelegram] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<Element | null>(null);

  // FM2 fix: move focus into the dialog on open and restore it to the trigger on close —
  // consistent with the shared Modal component's existing behavior (Modal.tsx).
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement;
      const raf = requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });
      return () => cancelAnimationFrame(raf);
    } else if (triggerRef.current instanceof HTMLElement) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [isOpen]);

  const loadUnits = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { units: loadedUnits } = await listUnits();
      setUnits(loadedUnits);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load units');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load tenants when unit is selected — every tenant with a live TenantUnitAssociation
  // (status 'active') is pre-selected by default (TG-11); the landlord can still deselect
  // individuals before submitting.
  const loadTenants = useCallback(async (unitId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const { tenants: loadedTenants } = await listTenantsByUnit(unitId);
      const liveTenants = loadedTenants.filter((t) => t.status === 'active');
      setTenants(liveTenants);
      setSelectedTenantIds(
        new Set(liveTenants.map((t) => t.identityId).filter((id): id is string => !!id))
      );
      setStep('tenants');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenants');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleUnitSelect = useCallback(
    (unitId: string) => {
      setSelectedUnitId(unitId);
      loadTenants(unitId);
    },
    [loadTenants]
  );

  // Load units on open; if opened with a unit already known (Unit Detail Page's "Message
  // this unit's tenants" link), skip straight to the tenant/Telegram step for that unit.
  useEffect(() => {
    if (isOpen) {
      loadUnits();
      if (initialUnitId) {
        handleUnitSelect(initialUnitId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialUnitId]);

  const handleTenantToggle = useCallback((tenantId: string) => {
    setSelectedTenantIds((prev) => {
      const next = new Set(prev);
      if (next.has(tenantId)) {
        next.delete(tenantId);
      } else {
        next.add(tenantId);
      }
      return next;
    });
  }, []);

  const resetToStart = useCallback(() => {
    setStep('unit');
    setSelectedUnitId('');
    setSelectedTenantIds(new Set());
    setConnectTelegram(false);
    setSearchQuery('');
  }, []);

  const handleCreate = useCallback(async () => {
    if (selectedTenantIds.size === 0) {
      setError('Please select at least one tenant');
      return;
    }

    try {
      setIsCreating(true);
      setError(null);

      const selectedUnit = units.find((u) => u.id === selectedUnitId);
      const conversationData: CreateConversationInput = {
        participantIdentityIds: Array.from(selectedTenantIds),
        unitId: selectedUnitId,
        propertyId: selectedUnit?.propertyId,
        portfolioId: selectedUnit?.property?.portfolioId,
        connectTelegram,
      };

      const conversation = await createConversation(conversationData);
      onConversationCreated?.(conversation.id);
      handleClose();
    } catch (err) {
      // BR-56: provisioning is atomic — a failure leaves no partial conversation and no
      // partial state to recover from. Reset the whole flow back to unit selection (rather
      // than letting the landlord retry with potentially stale roster/toggle state) and
      // surface the error clearly.
      resetToStart();
      setError(err instanceof Error ? err.message : 'Failed to create conversation. Please try again.');
    } finally {
      setIsCreating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantIds, selectedUnitId, units, connectTelegram, onConversationCreated, resetToStart]);

  const handleClose = useCallback(() => {
    resetToStart();
    setError(null);
    onClose();
  }, [onClose, resetToStart]);

  // FM2 fix: Escape closes the dialog, and Tab/Shift+Tab cycle within it — consistent with
  // the shared Modal component's existing focus-trap behavior (Modal.tsx).
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!isCreating) handleClose();
        return;
      }

      if (e.key !== 'Tab' || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
      ).filter((el) => el.offsetParent !== null);

      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [isCreating, handleClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  const handleBack = useCallback(() => {
    setStep('unit');
    setSelectedTenantIds(new Set());
    setConnectTelegram(false);
    setError(null);
  }, []);

  if (!isOpen) return null;

  const filteredUnits = units.filter((unit) => {
    const searchLower = searchQuery.toLowerCase();
    const propertyName = unit.propertyName ?? unit.property?.name ?? '';
    return (
      propertyName.toLowerCase().includes(searchLower) ||
      (realUnitNumber(unit.unitNumber)?.toLowerCase().includes(searchLower) ?? false) ||
      unit.address1.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={isCreating ? undefined : handleClose}
      data-testid="new-conversation-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-conversation-title"
    >
      <div
        ref={dialogRef}
        className="bg-card border border-border rounded-lg w-[90%] max-w-2xl max-h-[80vh] overflow-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 id="new-conversation-title" className="m-0 text-xl font-semibold text-foreground">
            {step === 'unit' ? 'Select Unit' : 'Select Tenants'}
          </h2>
          <button
            ref={closeButtonRef}
            onClick={handleClose}
            disabled={isCreating}
            className="bg-transparent border-0 text-2xl cursor-pointer text-muted-foreground hover:text-foreground min-h-[44px] px-2 disabled:opacity-50"
            aria-label="Close"
            data-testid="close-dialog"
          >
            ×
          </button>
        </div>

        {error && (
          <div
            className="p-3 mb-4 text-sm bg-destructive/5 border border-destructive/20 rounded text-destructive"
            role="alert"
            data-testid="error-message"
          >
            {error}
          </div>
        )}

        {step === 'unit' && (
          <>
            <input
              type="text"
              placeholder="Search units..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full p-3 mb-4 border border-input rounded text-base focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="unit-search-input"
            />

            {isLoading ? (
              <div className="text-center py-6">
                <p className="text-sm text-muted-foreground">Loading units...</p>
              </div>
            ) : (
              <div className="max-h-[400px] overflow-auto">
                {filteredUnits.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground">
                    No units found
                  </p>
                ) : (
                  filteredUnits.map((unit) => (
                    <button
                      key={unit.id}
                      onClick={() => handleUnitSelect(unit.id)}
                      className="w-full p-4 mb-2 border border-border rounded bg-card text-left cursor-pointer hover:bg-accent transition-colors min-h-[44px]"
                      data-testid={`unit-option-${unit.id}`}
                    >
                      <div className="font-semibold text-sm mb-1">
                        {formatUnitLabel(unit.unitNumber)
                          ? `${unit.propertyName ?? unit.property?.name ?? ''} — ${formatUnitLabel(unit.unitNumber)}`.trimStart()
                          : (unit.propertyName ?? unit.property?.name ?? 'Single Family')}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {unit.address1}, {unit.city}, {unit.state}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </>
        )}

        {step === 'tenants' && (
          <>
            {isLoading ? (
              <div className="text-center py-6">
                <p className="text-sm text-muted-foreground">Loading tenants...</p>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground">
                    All current tenants are selected by default — deselect anyone who shouldn&apos;t be included.
                  </p>
                </div>

                <div className="max-h-[400px] overflow-auto mb-4">
                  {tenants.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground">
                      No active tenants in this unit
                    </p>
                  ) : (
                    tenants.map((tenant) => {
                      const isSelected = selectedTenantIds.has(tenant.identityId!);
                      const displayName = `${tenant.firstName || ''} ${tenant.lastName || ''}`.trim() || tenant.email;

                      return (
                        <label
                          key={tenant.id}
                          className={`flex items-center p-3 mb-2 border-2 rounded cursor-pointer transition-all ${
                            isSelected
                              ? 'border-primary bg-primary/5'
                              : 'border-border bg-card'
                          } ${isCreating ? 'opacity-50 pointer-events-none' : ''}`}
                          data-testid={`tenant-option-${tenant.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleTenantToggle(tenant.identityId!)}
                            disabled={isCreating}
                            className="mr-3"
                          />
                          <div>
                            <div className="text-sm font-semibold">{displayName}</div>
                            {tenant.email && (
                              <div className="text-xs text-muted-foreground">
                                {tenant.email}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>

                <label
                  className={`flex items-start gap-3 p-3 mb-4 border-2 rounded cursor-pointer transition-all ${
                    connectTelegram ? 'border-primary bg-primary/5' : 'border-border bg-card'
                  } ${isCreating ? 'opacity-50 pointer-events-none' : ''}`}
                  data-testid="connect-telegram-toggle"
                >
                  <input
                    type="checkbox"
                    checked={connectTelegram}
                    onChange={(e) => setConnectTelegram(e.target.checked)}
                    disabled={isCreating}
                    className="mt-0.5 mr-1"
                  />
                  <div>
                    <div className="text-sm font-semibold text-foreground">Connect Telegram</div>
                    <div className="text-xs text-muted-foreground">
                      Automatically create a Telegram group for this conversation and email every
                      selected participant a join link.
                    </div>
                  </div>
                </label>

                <div className="flex gap-3">
                  <button
                    onClick={handleBack}
                    disabled={isCreating}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-accent disabled:opacity-50 min-h-[44px]"
                    data-testid="back-button"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleCreate}
                    disabled={selectedTenantIds.size === 0 || isCreating}
                    className="flex-[2] px-4 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 min-h-[44px]"
                    data-testid="create-conversation-button"
                  >
                    {isCreating
                      ? (connectTelegram ? 'Connecting Telegram...' : 'Creating...')
                      : 'Start Conversation'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
